import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { NotificationPanel } from './NotificationPanel'
import {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteNotification,
} from '../hooks/useNotifications'
import type { UserNotification } from '../types'
import { resolveActionUrl } from '../lib/resolveActionUrl'
import { useAuthStore } from '@/stores/authStore'
// Deep imports on purpose: the feature's index also pulls in its page components, which would
// land in the app shell's bundle through this header component.
import {
  usePlatformMessages,
  useMarkPlatformMessageRead,
} from '@/features/notification-channels/components/NotificationFeed/hooks/useNotificationFeed'
import type { PlatformMessage } from '@/features/notification-channels/types'

/** How many unread broadcasts the dropdown lists; the rest are one click away on the page. */
const MAX_BROADCASTS_SHOWN = 3

export function NotificationBell() {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)

  // Fetch notifications and unread count. LIVE-26: the list (up to 20 rows) is
  // fetched only while the dropdown is open — it used to run unconditionally,
  // so it refetched on every WebSocket notification.created invalidation even
  // with the panel closed and nobody able to see it, which is exactly the
  // load a duplicate-alert flood turns into a request storm. The unread badge
  // must still work while closed, so its query stays enabled; only its 60s
  // fallback poll timer is gated to "while open" (real-time updates otherwise
  // come from the same WebSocket invalidation).
  const { data: notificationsData, isLoading } = useNotifications({ limit: 20 }, { enabled: isOpen })
  const { data: countData } = useUnreadCount({ pollWhileOpen: isOpen })

  // Mutations
  const markAsRead = useMarkAsRead()
  const markAllAsRead = useMarkAllAsRead()
  const deleteNotification = useDeleteNotification()

  const notifications = notificationsData?.data ?? []
  const unreadCount = countData?.count ?? 0

  // Platform broadcasts are a tenant feature (the platform console has no tenant to be targeted),
  // and they used to be readable only on the Notifications page, which has no sidebar entry. So
  // they count in the badge and are listed here, otherwise a tenant never learns one was sent.
  const isTenantUser = !!useAuthStore((s) => s.user?.tenantId)
  const { data: broadcastData } = usePlatformMessages({ enabled: isTenantUser, poll: true })
  const markBroadcastRead = useMarkPlatformMessageRead()
  const unreadBroadcasts = isTenantUser ? (broadcastData?.data ?? []).filter((m) => !m.read) : []
  const broadcastUnreadTotal = isTenantUser ? (broadcastData?.unread_count ?? unreadBroadcasts.length) : 0
  const broadcastTotal = isTenantUser ? (broadcastData?.total ?? 0) : 0
  const badgeCount = unreadCount + broadcastUnreadTotal

  const handleBroadcastClick = (message: PlatformMessage) => {
    markBroadcastRead.mutate(message.id)
    navigate('/tenant/notifications?tab=broadcasts')
    setIsOpen(false)
  }

  const handleMarkAsRead = (id: string) => {
    markAsRead.mutate(id)
  }

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate()
  }

  const handleDelete = (id: string) => {
    deleteNotification.mutate(id)
  }

  const handleNotificationClick = (notification: UserNotification) => {
    if (notification.action_url) {
      // Handle relative and absolute URLs. Resolve scope first so legacy
      // unscoped approval deep-links (`/approvals/{id}`) route to the real
      // `/tenant/approvals/{id}` (or `/platform/...`) page instead of 404ing.
      const target = resolveActionUrl(notification.action_url)
      if (target.startsWith('/')) {
        navigate(target)
      } else {
        window.open(target, '_blank')
      }
    }
    setIsOpen(false)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  // Format the badge display
  const badgeText = badgeCount > 99 ? '99+' : String(badgeCount)

  return (
    // Radix Popover (same primitive as SupportButton) instead of a hand-rolled
    // `{isOpen && <div>}` conditional: Radix keeps the node mounted until the
    // data-[state=closed] exit animation finishes, so open AND close animate
    // (the conditional-mount version unmounted before any exit could play),
    // and its dismissable layer replaces the manual mousedown/Escape listeners
    // this component previously duplicated. Reduced motion: the global
    // animations.css clamp zeroes these animations — instant swap, by design.
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          data-tour-id="bell-icon"
          className="
            relative p-2 rounded-lg
            text-gray-500 hover:text-gray-700 hover:bg-gray-100
            dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800
            transition-colors
          "
          aria-label={`Notifications${badgeCount > 0 ? ` (${badgeCount} unread)` : ''}`}
        >
          <Bell className="h-5 w-5" />

          {/* Unread badge */}
          {badgeCount > 0 && (
            <span className="
              absolute -top-0.5 -right-0.5
              min-w-[18px] h-[18px] px-1
              flex items-center justify-center
              text-[10px] font-bold text-white
              bg-red-500 rounded-full
            ">
              {badgeText}
            </span>
          )}
        </button>
      </PopoverTrigger>
      {/* NotificationPanel carries its own card chrome — strip PopoverContent's
          default width/padding/border so it is purely the animated positioning
          shell, exactly like SupportButton's usage. */}
      <PopoverContent align="end" sideOffset={8} className="w-auto border-0 bg-transparent p-0 shadow-none">
        <NotificationPanel
          notifications={notifications}
          isLoading={isLoading}
          unreadCount={unreadCount}
          onMarkAsRead={handleMarkAsRead}
          onMarkAllAsRead={handleMarkAllAsRead}
          onDelete={handleDelete}
          onNotificationClick={handleNotificationClick}
          onClose={handleClose}
          broadcasts={unreadBroadcasts.slice(0, MAX_BROADCASTS_SHOWN)}
          broadcastUnreadTotal={broadcastUnreadTotal}
          broadcastTotal={broadcastTotal}
          onBroadcastClick={handleBroadcastClick}
        />
      </PopoverContent>
    </Popover>
  )
}
